import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const field =
  "w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 text-sm text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-900 focus:ring-1 focus:ring-zinc-900 transition-colors";

export default function Auth() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
      } else {
        await register(form.name, form.email, form.password);
      }
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate("/shop");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="pt-28 pb-24 min-h-screen flex items-center">
      <div className="max-w-md mx-auto w-full px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-8">
            <div className="font-display text-2xl font-extrabold tracking-tighter">
              Vsense<span className="text-zinc-400">.</span>
            </div>
            <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tighter text-zinc-900">
              {mode === "login" ? "Welcome back" : "Create your account"}
            </h1>
            <p className="mt-2 text-sm text-zinc-500">
              {mode === "login"
                ? "Sign in to track your orders and checkout faster."
                : "Save your details and order history with Vsense."}
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="auth-form">
            <AnimatePresence mode="popLayout">
              {mode === "register" && (
                <motion.input
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  required
                  data-testid="auth-name"
                  className={field}
                  placeholder="Full name"
                  value={form.name}
                  onChange={set("name")}
                />
              )}
            </AnimatePresence>
            <input required type="email" data-testid="auth-email" className={field} placeholder="Email" value={form.email} onChange={set("email")} />
            <input required type="password" minLength={6} data-testid="auth-password" className={field} placeholder="Password" value={form.password} onChange={set("password")} />

            {error && (
              <p data-testid="auth-error" className="text-sm text-red-600">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="auth-submit"
              className="w-full bg-zinc-900 text-white text-sm font-medium py-3.5 rounded-full hover:bg-zinc-800 active:scale-[0.99] transition-all duration-200 disabled:opacity-60"
            >
              {loading ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-zinc-500">
            {mode === "login" ? "New to Vsense? " : "Already have an account? "}
            <button
              data-testid="auth-toggle"
              onClick={() => {
                setMode(mode === "login" ? "register" : "login");
                setError("");
              }}
              className="text-zinc-900 font-medium underline underline-offset-4"
            >
              {mode === "login" ? "Create an account" : "Sign in"}
            </button>
          </div>

          <div className="mt-4 text-center">
            <Link to="/shop" data-testid="guest-checkout-link" className="text-sm text-zinc-400 hover:text-zinc-700 transition-colors">
              Continue as guest
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
